<template>
  <div
    class="student-dashboard"
    :class="{ 'student-dashboard--rich': minimalHome }"
  >
    <!--
      P3 (student-foundation-redesign) -- rich Home. The legacy photo-banner
      DashboardBanner is preserved byte-for-byte for the non-minimal (legacy)
      path; minimal-home students get the near-final StudentHomeRich body
      instead (supersedes TASK-SF-11's MinimalHomeWelcome, per the explicit
      post-Foundation authorization recorded in
      .claude/plans/student-foundation-redesign/00_SCOPE_AND_DESIGN_REFERENCE.md
      §10.4). Flipping `minimalHome` to false still restores DashboardBanner
      for everyone; P3's own rollback is reverting its single commit.
    -->
    <dashboard-banner
      v-if="!minimalHome"
      :subheader="
        showDashboardRedesign
          ? $t('dashboard.studentDashboard.dashboardRedesign')
          : ``
      "
    />
    <student-home-rich v-else />
    <div class="dashboard-notices">
      <div
        v-if="downtimeBannerMessage"
        class="dashboard-notice"
        :class="'dashboard-notice--downtime'"
      >
        <a href="https://upchieve.statuspage.io" target="_blank">{{
          downtimeBannerMessage
        }}</a>
      </div>
    </div>

    <!--
      TASK-SF-11 (D-05 / D-06) -- Minimal student Home. ONE reversible render
      condition (`minimalHome`) suppresses SubjectSelection, the
      StudentOnboardingModal carousel and the four promotional modals.
      Nothing is deleted: set `minimalHome` to false to restore the full
      dashboard exactly as it was. The volunteer dashboard is a different
      component and is untouched.
    -->
    <template v-if="!minimalHome">
      <tell-them-college-prep-modal
        v-if="showTellThemCollegePrepModal"
        :closeModal="toggleTellThemCollegePrepModal"
      />
      <procrastination-prevention-modal
        v-if="showThemProcrastinationPreventionModal"
        :closeModal="toggleProcrastinationPreventionModal"
      />
      <fall-incentive-enrollment-modal
        v-if="showFallIncentiveEnrollmentModal"
        :closeModal="toggleFallIncentiveEnrollmentModal"
      />
      <scorecaster-modal
        v-if="showScorecasterModal"
        :closeModal="toggleShowScorecasterModal"
      />
      <subject-selection />
    </template>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import DashboardBanner from '../DashboardBanner'
import StudentHomeRich from '@/components/StudentHome/StudentHomeRich.vue'
import SubjectSelection from './SubjectSelection/index.vue'
import TellThemCollegePrepModal from './TellThemCollegePrepModal.vue'
import ProcrastinationPreventionModal from './ProcrastinationPreventionModal.vue'
import FallIncentiveEnrollmentModal from './FallIncentiveEnrollmentModal.vue'
import StudentOnboardingModal from './StudentOnboardingModal.vue'
import ScorecasterModal from './ScorecasterModal.vue'
import AnalyticsService from '@/services/AnalyticsService'
import ProductDiscoveryService from '@/services/ProductDiscoveryService'
import { EVENTS, VERIFICATION_METHOD } from '@/consts'
import getCookie from '@/utils/get-cookie'

const defaultHeaderData = {
  component: 'DefaultHeader',
}

const activeHeaderData = {
  component: 'RejoinSessionHeader',
}

const bannedHeaderData = {
  component: 'BannedHeader',
}

// TODO: abstract this banner out more to allow for dynamic targeting
const dashboardBannerData = {
  component: 'DashboardBannerHeader',
}

export default {
  name: 'student-dashboard',
  components: {
    DashboardBanner,
    StudentHomeRich,
    SubjectSelection,
    TellThemCollegePrepModal,
    ProcrastinationPreventionModal,
    FallIncentiveEnrollmentModal,
    ScorecasterModal,
  },
  async created() {
    if (this.isSessionAlive) {
      this.$store.dispatch('app/header/show', activeHeaderData)
    } else if (
      !this.user.emailVerified &&
      this.isSmsVerificationEnabledOnSignupFlow
    ) {
      this.$store.dispatch('app/header/show', {
        component: 'VerificationHeader',
        data: {
          verificationMethod: VERIFICATION_METHOD.EMAIL,
          phoneOrEmailToVerify: this.user.email,
        },
      })
    } else if (this.isDashboardBannerActive) {
      this.triggerIncentiveProgram()
    }

    // TASK-SF-11 (D-05 / D-06): the SAME single `minimalHome` condition that
    // suppresses the body markup also skips every promotional-modal,
    // onboarding-carousel and product-segment trigger below. The banned
    // header stays -- it is a safety notice, not Home content. Flip
    // `minimalHome` to false to restore the full dashboard behaviour.
    if (this.minimalHome) {
      if (this.user && this.user.isBanned) {
        this.$store.dispatch('app/header/show', bannedHeaderData)
      }
      return
    }

    if (this.isFirstDashboardVisit) {
      this.$store.dispatch('app/modal/show', {
        component: StudentOnboardingModal,
        data: {
          showTemplateButtons: false,
        },
      })
    }

    if (localStorage.getItem('isSSOSignUpRedirect')) {
      AnalyticsService.captureEvent(EVENTS.ACCOUNT_CREATED)
      AnalyticsService.captureEvent(EVENTS.ACCOUNT_VERIFIED)
      localStorage.removeItem('isSSOSignUpRedirect')
      this.$store.dispatch('user/firstDashboardVisit', true)
      this.$store.dispatch('app/modal/show', {
        component: StudentOnboardingModal,
        data: {
          showTemplateButtons: false,
        },
      })
    }

    if (this.isOrbitalSegmentsActive)
      ProductDiscoveryService.triggerDynamicSegment(
        this.user,
        this.orbitalSegments
      )

    if (
      this.isCollegePrepAdActive[0] &&
      this.isCollegePrepAdActive[1] &&
      !getCookie('hasSeenTellThemCollegePrepModal') &&
      this.pastSessionsCount >= 1
    )
      this.showTellThemCollegePrepModal = true

    if (
      this.isProcrastinationPreventionReminderActive[0] &&
      this.isProcrastinationPreventionReminderActive[1] &&
      !localStorage.getItem('hasSeenProcrastinationPreventionModal')
    )
      this.showThemProcrastinationPreventionModal = true

    if (this.isEnrollmentForFallIncentiveModalActive)
      this.showFallIncentiveEnrollmentModal = true

    if (this.isScorecasterUserSegment) this.showScorecasterModal = true

    // TODO: move globally to show banner in all pages
    if (this.user && this.user.isBanned) {
      this.$store.dispatch('app/header/show', bannedHeaderData)
    }
  },
  data() {
    return {
      showTellThemCollegePrepModal: false,
      showThemProcrastinationPreventionModal: false,
      showFallIncentiveEnrollmentModal: false,
      showScorecasterModal: false,
    }
  },
  computed: {
    // TASK-SF-11 (D-05 / D-06): the ONE reversible condition for the minimal
    // student Home. Intentionally a constant for this phase; making it false
    // (or deleting it and the two `!minimalHome` / `if (this.minimalHome)`
    // guards) restores the full dashboard with nothing else to undo.
    minimalHome() {
      return true
    },
    ...mapState({
      user: state => state.user.user,
      hadASession: state => state.user.hadASession,
      prevSessionSubject: state => state.user.prevSessionSubject,
      isFirstDashboardVisit: state => state.user.isFirstDashboardVisit,
      productFlags: state => state.productFlags.flags,
    }),
    ...mapGetters({
      isSessionAlive: 'user/isSessionAlive',
      downtimeBannerMessage: 'featureFlags/downtimeBannerMessage',
      orbitalSegments: 'featureFlags/orbitalSegments',
      isOrbitalSegmentsActive: 'featureFlags/isOrbitalSegmentsActive',
      isJustTellThemActive: 'featureFlags/isJustTellThemActive',
      isProcrastinationPreventionActive:
        'featureFlags/isProcrastinationPreventionActive',
      isDashboardBannerActive: 'featureFlags/isDashboardBannerActive',
      isFallIncentiveEnrollmentActive:
        'featureFlags/isFallIncentiveEnrollmentActive',
      showDashboardRedesign: 'user/showDashboardRedesign',
      isScorecasterModalActive: 'featureFlags/isScorecasterModalActive',
      isAutoStartCollegeSessionActive:
        'featureFlags/isAutoStartCollegeSessionActive',
      autoStartCollegeSession: 'featureFlags/autoStartCollegeSession',
      isSmsVerificationEnabledOnSignupFlow:
        'featureFlags/isSmsVerificationEnabledOnSignupFlow',
    }),
    pastSessionsCount() {
      return Array.isArray(this.user.pastSessions)
        ? this.user.pastSessions.length
        : 0
    },
    userAndOrbitalSegment() {
      return [this.user, this.orbitalSegments, this.isOrbitalSegmentsActive]
    },
    isCollegePrepAdActive() {
      return [this.isJustTellThemActive, this.hadASession]
    },
    isFallIncentiveProgramActive() {
      return [this.user, this.productFlags, this.isDashboardBannerActive]
    },
    isProcrastinationPreventionReminderActive() {
      return [this.isProcrastinationPreventionActive, this.hadASession]
    },
    isEnrollmentForFallIncentiveActive() {
      return [this.isFallIncentiveEnrollmentActive, this.hadASession]
    },
    isEnrollmentForFallIncentiveModalActive() {
      return (
        this.isEnrollmentForFallIncentiveActive[0] &&
        this.isEnrollmentForFallIncentiveActive[1] &&
        !localStorage.getItem('hasSeenFallIncentiveEnrollmentModal') &&
        this.pastSessionsCount === 1 &&
        !this.user.phone
      )
    },
    isScorecasterUserSegment() {
      const hadAReadingSession = this.prevSessionSubject === 'reading'
      return (
        this.isScorecasterModalActive &&
        hadAReadingSession &&
        !localStorage.getItem('hasSeenScorecasterModal')
      )
    },
  },
  methods: {
    toggleTellThemCollegePrepModal() {
      this.showTellThemCollegePrepModal = !this.showTellThemCollegePrepModal
    },
    toggleProcrastinationPreventionModal() {
      this.showThemProcrastinationPreventionModal = !this
        .showThemProcrastinationPreventionModal
    },
    toggleFallIncentiveEnrollmentModal() {
      this.showFallIncentiveEnrollmentModal = !this
        .showFallIncentiveEnrollmentModal
    },
    toggleShowScorecasterModal() {
      this.showScorecasterModal = !this.showScorecasterModal
    },
    // Only show the banner to those who have >= 1 sessions and < 10 session or in the incentive program
    triggerIncentiveProgram() {
      if (
        !this.productFlags.fallIncentiveProgram &&
        (this.pastSessionsCount < 1 || this.pastSessionsCount > 9)
      )
        return

      this.$store.dispatch('app/header/show', dashboardBannerData)
    },
  },
  watch: {
    isFirstDashboardVisit(currValue, prevValue) {
      const hasJustDismissedWelcomeModal = !currValue && prevValue
      if (
        hasJustDismissedWelcomeModal &&
        !this.hadASession &&
        this.isAutoStartCollegeSessionActive &&
        this.autoStartCollegeSession
      ) {
        this.$store.dispatch('app/modal/show', {
          component: 'StartCollegeSessionModal',
          data: {
            showTemplateButtons: false,
            sessionTopic: this.autoStartCollegeSession,
          },
        })
      }
    },
    isSessionAlive(isAlive) {
      if (!isAlive) {
        this.$store.dispatch('app/header/show', defaultHeaderData)
      } else {
        this.$store.dispatch('app/header/show', activeHeaderData)
      }
    },
    isCollegePrepAdActive(currentValue, prevValue) {
      if (
        currentValue[0] &&
        currentValue[1] &&
        (!prevValue[0] || !prevValue[1]) &&
        !getCookie('hasSeenTellThemCollegePrepModal') &&
        this.pastSessionsCount >= 1
      ) {
        this.showTellThemCollegePrepModal = true
      }
    },
    isProcrastinationPreventionReminderActive(currentValue, prevValue) {
      if (
        currentValue[0] &&
        currentValue[1] &&
        (!prevValue[0] || !prevValue[1]) &&
        !localStorage.getItem('hasSeenProcrastinationPreventionModal') &&
        this.pastSessionsCount >= 1
      ) {
        this.showThemProcrastinationPreventionModal = true
      }
    },
    userAndOrbitalSegment: {
      handler: function(currentValue, prevValue) {
        if (
          this.isOrbitalSegmentsActive &&
          Object.keys(currentValue[0]).length &&
          currentValue[1].length &&
          currentValue[2] &&
          (!Object.keys(prevValue[0]).length ||
            !prevValue[1].length ||
            !prevValue[2])
        )
          ProductDiscoveryService.triggerDynamicSegment(
            this.user,
            this.orbitalSegments
          )
      },
      deep: true,
    },
    isFallIncentiveProgramActive: {
      handler: function(currentValue, prevValue) {
        if (
          Object.keys(currentValue[0]).length &&
          Object.keys(currentValue[1]).length &&
          currentValue[2] &&
          !this.isSessionAlive &&
          (!Object.keys(prevValue[0]).length ||
            !Object.keys(prevValue[1]).length ||
            !prevValue[2])
        )
          this.triggerIncentiveProgram()
      },
      deep: true,
    },
    isEnrollmentForFallIncentiveActive: {
      handler: function(currentValue, prevValue) {
        if (
          (currentValue[0] && currentValue[1] && !prevValue[0]) ||
          !prevValue[1]
        )
          if (this.isEnrollmentForFallIncentiveModalActive)
            this.showFallIncentiveEnrollmentModal = true
      },
      deep: true,
    },
    isScorecasterUserSegment(currentValue, prevValue) {
      if (currentValue && !prevValue) this.showScorecasterModal = true
    },
  },
}
</script>

<style lang="scss" scoped>
.student-dashboard {
  @include flex-container(column);
  padding: 40px 20px;

  @include breakpoint-above('medium') {
    display: inline-flex;
    min-width: 100%;
    padding: 40px;
  }
}

// P3 full-shell visual remediation (student-foundation-redesign, 00_*.md
// §13): matches the rendered prototype's `.home-screen` max-width/centering
// (00_*.md §13.1) -- at wide viewports Home content caps at 980px and
// centers, instead of stretching full width. Scoped to the `minimalHome`
// (rich Home) path only via this modifier class; the dormant legacy
// DashboardBanner path above keeps its original full-width padding
// untouched, exactly as its own comment already promises.
.student-dashboard--rich {
  // Student Journey V2: the SAME page box the tutor setup uses (34/38 on
  // desktop, 6/16 in the mobile shell, 13px sides on phones), around V2's
  // 980px `.home-screen` column. Home used to carry its own 20px gutter and
  // 34px top offset on mobile, so it sat 7px / 28px off the setup screen it
  // leads into.
  @include borna-page-container(980px);
  display: flex;

  @include breakpoint-above('medium') {
    display: flex;
    min-width: 0;
  }
}

.dashboard-notice {
  padding: 15px;
  background-color: $c-success-green;
  border-radius: 8px;
  margin-top: 20px;
  font-weight: 500;
  font-size: 16px;
  color: #fff;
  text-align: center;

  a {
    color: #fff;

    &:hover {
      color: #f3f3f3;
      text-decoration: none;
    }
  }

  &:last-child {
    // TODO: a cleaner way to handle spacing issues with class SubjectSelection
    margin-bottom: -20px;
  }

  &--warn {
    background-color: $c-warning-orange;
  }

  &--info {
    background-color: $c-information-blue;
  }

  &--downtime {
    color: #fff;
    background-color: $c-information-blue;
  }

  &--summer-prep {
    color: #fff;
    background-color: $c-information-blue;
    a {
      text-decoration: underline;
      font-weight: bold;
    }
  }
}
</style>
